import { useGetJobsiteConfigSiteLayoutQuery } from 'apollo/generated/client-operations';
import { AuthContext } from 'auth';
import {
  JobsiteConfigTabApi,
  JobsiteConfigTabProps,
} from 'containers/jobsiteConfiguration/types';
import { GeofencingTab } from 'containers/jobsiteConfiguration/tabs/geofencing';
import { useFlags } from 'featureFlags';
import React from 'react';
import { useSitePermissions } from 'sitePermissions/useSitePermissions';
import { LayoutVersionsPanel } from './LayoutVersionsPanel';
import { siteLayoutSections } from './siteLayoutSections';

/**
 * The Site Layout tab: the geofencing form, then the site's layout versions
 * where the zones list stood. The layout itself is edited on its own page,
 * the designer, so the tab's Save Changes only ever applies to the form.
 * Each section shows only to a user its own gate lets in
 * (`siteLayoutSections`).
 */
export function SiteLayoutTab(
  props: JobsiteConfigTabProps,
): React.ReactElement {
  const { jobsiteId, onTabApiChange, onIsDirtyChange } = props;
  const { currentUser: user } = React.useContext(AuthContext);
  const { siteLayoutUiEnabled } = useFlags();
  const sitePermissions = useSitePermissions(jobsiteId);
  const { geofencing, versions } = siteLayoutSections({
    siteLayoutUiEnabled: Boolean(siteLayoutUiEnabled),
    jobsiteId,
    user,
    sitePermissions,
  });
  const versionsApi = React.useRef<{ refetch: () => void }>({
    refetch: () => undefined,
  });
  const onVersionsApiChange = React.useCallback(
    (api: { refetch: () => void }): void => {
      versionsApi.current = api;
    },
    [],
  );

  // The header's reload refreshes the versions with the geofencing form.
  // Geofencing also reports its form's `submit` on its own, without a reload,
  // so only a reported reload is wrapped.
  const onGeofencingTabApiChange = React.useCallback(
    (api: Partial<JobsiteConfigTabApi>): void =>
      onTabApiChange?.(
        api.refetchData
          ? {
              ...api,
              refetchData: (): void => {
                api.refetchData();
                versionsApi.current.refetch();
              },
            }
          : api,
      ),
    [onTabApiChange],
  );

  // Without the geofencing form, the tab names the site and reloads the
  // versions itself, and has nothing for Save Changes.
  const { data: siteData, refetch: refetchSite } =
    useGetJobsiteConfigSiteLayoutQuery({
      variables: { jobsiteId },
      skip: geofencing,
      fetchPolicy: 'no-cache',
    });
  const siteName = siteData?.getJobsite?.name;
  React.useEffect(() => {
    if (!geofencing)
      onTabApiChange?.({
        refetchData: (): void => {
          refetchSite();
          versionsApi.current.refetch();
        },
        loading: false,
        jobsiteName: siteName,
      });
  }, [geofencing, onTabApiChange, refetchSite, siteName]);
  React.useEffect(() => {
    if (!geofencing) onIsDirtyChange?.(false);
  }, [geofencing, onIsDirtyChange]);

  return (
    <>
      {geofencing && (
        <GeofencingTab {...props} onTabApiChange={onGeofencingTabApiChange} />
      )}
      {versions && (
        <LayoutVersionsPanel
          jobsiteId={jobsiteId}
          onApiChange={onVersionsApiChange}
        />
      )}
    </>
  );
}
